import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth.service';
import type { UserRole } from '@/types/user.types';

interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
  showLoading?: boolean;
  requiredRoles?: UserRole[];
}

export default function ProtectedRoute({
  children,
  redirectTo = '/login',
  showLoading = true,
  requiredRoles,
}: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const [profileFailed, setProfileFailed] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !user && !profileFailed) {
      authService.getProfile()
        .then((profile) => {
          const token = useAuthStore.getState().token;
          if (token && profile) {
            useAuthStore.getState().login(token, profile);
          } else {
            setProfileFailed(true);
            useAuthStore.getState().logout();
          }
        })
        .catch(() => {
          setProfileFailed(true);
          useAuthStore.getState().logout();
        });
    }
  }, [isAuthenticated, user, profileFailed]);

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Authenticated but user data not loaded yet
  if (showLoading && !user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // must_change_password redirect — skip if already on /password
  if (user?.must_change_password && location.pathname !== '/password') {
    return <Navigate to="/password" replace />;
  }

  // Role check — redirect to overview if user doesn't have required role
  if (requiredRoles && user && !requiredRoles.includes(user.role)) {
    return <Navigate to="/overview" replace />;
  }

  // Authenticated and authorized - render children
  return <>{children}</>;
}

export function PublicRoute({
  children,
  redirectTo = '/overview',
}: {
  children: ReactNode;
  redirectTo?: string;
}) {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
